import { EditTwoTone } from "@ant-design/icons";
import {
  Button,
  Checkbox,
  Descriptions,
  Form,
  Input,
  Radio,
  Select,
  Space,
  Tag,
  theme,
} from "antd";
import React, { useEffect, useState } from "react";
import PracticesService, {
  IPracticeEmailInitialValues,
  PracticeUpdateTypes,
} from "../../Services/PracticesService";
import { Utils } from "../../Utilities/Utils";
import { adminContext } from "../../Common/PageRoute";
import { useUser } from "../../Context/UserContext";
import { AppDrawer } from "../../Common/Layout";

interface EmailSettingsProps {
  isEdit: { type: PracticeUpdateTypes };
  setIsEdit: React.Dispatch<
    React.SetStateAction<{ type: PracticeUpdateTypes }>
  >;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  practiceId: number;
}

const EmailSettings = ({
  isEdit,
  setIsEdit,
  setLoading,
  practiceId,
}: EmailSettingsProps) => {
  const { isSubAdmin } = useUser();
  const isViewOnly = isSubAdmin();
  const { token } = theme.useToken();
  const { openNotification } = React.useContext(adminContext);

  const [form] = Form.useForm();
  const [data, setData] = useState<IPracticeEmailInitialValues>();
  const [ccOptions, setCCOptions] = useState<any[]>([]);
  const [bccOptions, setBCCOptions] = useState<any[]>([]);
  const [saving, setSaving] = useState(false);

  const getEmailInformation = async () => {
    try {
      setLoading(true);
      const res =
        await PracticesService.getPracticeTimeVatEmailInformation(practiceId);
      if (res?.result && res?.status) {
        const fieldValues: IPracticeEmailInitialValues = {
          emailProvider: res.result.emailProvider ?? 0,
          defaultBCCs: res.result.defaultBCCs || [],
          defaultCCs: res.result.defaultCCs || [],
          noReply: {
            enabled: !!res.result.noReply?.enabled,
            replyEmail: res.result.noReply?.replyEmail || "",
          },
        };
        setData(fieldValues);
        form.setFieldsValue(fieldValues);

        if (fieldValues.defaultCCs?.length) {
          setCCOptions(
            fieldValues.defaultCCs.map((email: string) => ({
              name: email,
              email,
            })),
          );
        }
        if (fieldValues.defaultBCCs?.length) {
          setBCCOptions(
            fieldValues.defaultBCCs.map((email: string) => ({
              name: email,
              email,
            })),
          );
        }
      } else {
        openNotification(
          "error",
          res?.message || "Error fetching email settings",
        );
      }
    } catch {
      openNotification("error", "Error fetching email settings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (practiceId) {
      getEmailInformation();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [practiceId]);

  const isEditing = isEdit.type === PracticeUpdateTypes.emails;

  const handleOpenEdit = () => {
    if (data) {
      form.setFieldsValue({
        emailProvider: data.emailProvider ?? 0,
        defaultCCs: data.defaultCCs || [],
        defaultBCCs: data.defaultBCCs || [],
        noReply: {
          enabled: !!data.noReply?.enabled,
          replyEmail: data.noReply?.replyEmail || "",
        },
      });
    }
    setIsEdit({ type: PracticeUpdateTypes.emails });
  };

  const handleCancel = () => {
    if (data) {
      form.setFieldsValue(data);
    }
    setIsEdit({ type: PracticeUpdateTypes.unknown });
  };

  const getToItems = async (filter: string, practiceId: number) => {
    if (filter) {
      const result = await PracticesService.search(practiceId, filter, 0);
      return result?.items || [];
    }
    return [];
  };

  const handleSearch = async (value: string) => {
    const res = await getToItems(value, practiceId);
    setCCOptions(res || []);
    setBCCOptions(res || []);
  };

  const saveEmailDetails = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      setLoading(true);

      const params: IPracticeEmailInitialValues = {
        emailProvider: values.emailProvider,
        defaultBCCs: values.defaultBCCs || [],
        defaultCCs: values.defaultCCs || [],
        noReply: {
          enabled: !!values.noReply?.enabled,
          replyEmail: values.noReply?.replyEmail || "",
        },
      };

      const res = await PracticesService.updatePracticeEmail(
        practiceId,
        params,
      );

      if (res?.result) {
        openNotification("success", "Email settings updated successfully");
        setIsEdit({ type: PracticeUpdateTypes.unknown });
        await getEmailInformation();
      } else {
        openNotification(
          "error",
          res?.message || "Error updating email settings",
        );
      }
    } catch {
      openNotification("error", "Error updating email settings");
    } finally {
      setSaving(false);
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        background: token.colorBgContainer,
        borderRadius: token.borderRadiusLG,
        padding: "16px 20px",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          paddingBottom: 12,
          marginBottom: 14,
          borderBottom: `1px solid ${token.colorBorderSecondary}`,
        }}
      >
        <div>
          <span
            style={{
              fontSize: 14,
              fontWeight: 600,
              color: token.colorTextHeading,
            }}
          >
            Email Provider & Routing
          </span>
          <div
            style={{
              fontSize: 11,
              color: token.colorTextSecondary,
              marginTop: 1,
            }}
          >
            Default mail dispatch service, default CC/BCC recipients, and
            no-reply routing.
          </div>
        </div>

        {!isViewOnly && (
          <Button
            size="small"
            type="text"
            shape="circle"
            icon={<EditTwoTone />}
            onClick={handleOpenEdit}
          />
        )}
      </div>

      {/* Unbordered Descriptions */}
      <Descriptions
        size="small"
        bordered={false}
        column={{ xs: 1, sm: 2, md: 3 }}
        labelStyle={{
          color: token.colorTextSecondary,
          fontWeight: 500,
          fontSize: 12,
        }}
        contentStyle={{
          color: token.colorText,
          fontWeight: 500,
          fontSize: 13,
        }}
      >
        <Descriptions.Item label="Email Service">
          {data?.emailProvider === 1 ? (
            <Tag color="blue" style={{ borderRadius: 4, margin: 0 }}>
              Outlook
            </Tag>
          ) : (
            <Tag color="red" style={{ borderRadius: 4, margin: 0 }}>
              Gmail
            </Tag>
          )}
        </Descriptions.Item>

        <Descriptions.Item label="No-Reply Email">
          {data?.noReply?.enabled ? (
            <Tag color="green" style={{ borderRadius: 4, margin: 0 }}>
              Enabled
            </Tag>
          ) : (
            <Tag color="default" style={{ borderRadius: 4, margin: 0 }}>
              Disabled
            </Tag>
          )}
        </Descriptions.Item>

        <Descriptions.Item label="Reply-To Email">
          {data?.noReply?.enabled && data?.noReply?.replyEmail ? (
            <a href={`mailto:${data.noReply.replyEmail}`}>
              {data.noReply.replyEmail}
            </a>
          ) : (
            "—"
          )}
        </Descriptions.Item>

        <Descriptions.Item label="Default CCs" span={3}>
          {data?.defaultCCs && data.defaultCCs.length > 0 ? (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {data.defaultCCs.map((email: string) => (
                <Tag
                  key={email}
                  color="geekblue"
                  style={{ borderRadius: 4, margin: 0 }}
                >
                  {email}
                </Tag>
              ))}
            </div>
          ) : (
            "—"
          )}
        </Descriptions.Item>

        <Descriptions.Item label="Default BCCs" span={3}>
          {data?.defaultBCCs && data.defaultBCCs.length > 0 ? (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {data.defaultBCCs.map((email: string) => (
                <Tag
                  key={email}
                  color="purple"
                  style={{ borderRadius: 4, margin: 0 }}
                >
                  {email}
                </Tag>
              ))}
            </div>
          ) : (
            "—"
          )}
        </Descriptions.Item>
      </Descriptions>

      {/* Standard Edit Drawer */}
      <AppDrawer
        title="Edit Email Settings"
        subtitle="Configure practice email service and default recipients"
        width={480}
        open={isEditing}
        onClose={handleCancel}
        onSave={saveEmailDetails}
        saveText="Save Changes"
        saveButtonProps={{ loading: saving }}
        cancelButtonProps={{ disabled: saving }}
      >
        <Form form={form} layout="vertical" disabled={saving}>
          <Form.Item
            label="Email Service"
            name="emailProvider"
            rules={[{ required: true, message: "Please select email service" }]}
          >
            <Radio.Group style={{ display: "flex", gap: 24 }}>
              <Radio value={0}>Gmail</Radio>
              <Radio value={1}>Outlook</Radio>
            </Radio.Group>
          </Form.Item>

          <Form.Item label="Default CCs" name="defaultCCs">
            <Select
              mode="tags"
              showSearch
              placeholder="Search or enter CC email addresses"
              options={ccOptions.map((item) => ({
                label: item?.name || item?.email,
                value: item?.email,
              }))}
              onSearch={handleSearch}
            />
          </Form.Item>

          <Form.Item label="Default BCCs" name="defaultBCCs">
            <Select
              mode="tags"
              showSearch
              placeholder="Search or enter BCC email addresses"
              options={bccOptions.map((item) => ({
                label: item?.name || item?.email,
                value: item?.email,
              }))}
              onSearch={handleSearch}
            />
          </Form.Item>

          <Form.Item
            valuePropName="checked"
            name={["noReply", "enabled"]}
            style={{ marginBottom: 16 }}
          >
            <Checkbox>Enable No-Reply Email</Checkbox>
          </Form.Item>

          <Form.Item
            shouldUpdate={(prev, cur) =>
              prev?.noReply?.enabled !== cur?.noReply?.enabled
            }
            noStyle
          >
            {({ getFieldValue }) =>
              getFieldValue(["noReply", "enabled"]) ? (
                <Form.Item
                  label="Reply-To Email Address"
                  name={["noReply", "replyEmail"]}
                  rules={[
                    {
                      required: true,
                      message: "Reply-To email address is required",
                    },
                    { validator: Utils.ValidateEmailFormat },
                  ]}
                >
                  <Input placeholder="no-reply@domain.com" />
                </Form.Item>
              ) : null
            }
          </Form.Item>
        </Form>
      </AppDrawer>
    </div>
  );
};

export default EmailSettings;
